/**
 * Create or edit your own food. You type what the label says — per serving —
 * and it's stored per 100 g, so any amount can be logged later. Prefilled from
 * a label read, which is the point: the AI reads the numbers, you confirm them.
 */

import { useState } from 'react'

import type { CustomFoodRow } from '../db'
import { saveCustomFood } from '../db/mutations'
import { clean, per100From, scale, type NutrientKey, type Nutrients } from '../lib/nutrients'

const FIELDS: { key: NutrientKey; label: string; unit: string; main?: boolean }[] = [
  { key: 'kcal', label: 'Calories', unit: 'kcal', main: true },
  { key: 'protein_g', label: 'Protein', unit: 'g', main: true },
  { key: 'carbs_g', label: 'Carbs', unit: 'g', main: true },
  { key: 'fat_g', label: 'Fat', unit: 'g', main: true },
  { key: 'fibre_g', label: 'Fibre', unit: 'g' },
  { key: 'sugars_g', label: 'Sugars', unit: 'g' },
  { key: 'added_sugars_g', label: 'Added sugars', unit: 'g' },
  { key: 'sat_fat_g', label: 'Saturated fat', unit: 'g' },
  { key: 'trans_fat_g', label: 'Trans fat', unit: 'g' },
  { key: 'sodium_mg', label: 'Sodium', unit: 'mg' },
  { key: 'cholesterol_mg', label: 'Cholesterol', unit: 'mg' },
  { key: 'potassium_mg', label: 'Potassium', unit: 'mg' },
  { key: 'calcium_mg', label: 'Calcium', unit: 'mg' },
  { key: 'iron_mg', label: 'Iron', unit: 'mg' },
]

export type CustomFoodDraft = {
  id?: string
  name: string
  brand?: string | null
  barcode?: string | null
  basis: 'g' | 'ml'
  serving_label: string
  serving_size: number | null
  per100: Nutrients
  source?: string | null
}

export function draftFromRow(r: CustomFoodRow): CustomFoodDraft {
  const s = r.servings?.find((x) => x.default) ?? r.servings?.[0]
  return { id: r.id, name: r.name, brand: r.brand, barcode: r.barcode, basis: r.basis,
    serving_label: s?.label ?? '', serving_size: s?.grams ?? null, per100: r.per100 ?? {}, source: r.source }
}

export function CustomFoodEditor({ initial, onSaved, note }: {
  initial: CustomFoodDraft
  onSaved: (row: CustomFoodRow) => void
  note?: string | null
}) {
  const size0 = initial.serving_size ?? 100
  const perServing0 = scale(initial.per100, size0)
  const [name, setName] = useState(initial.name)
  const [brand, setBrand] = useState(initial.brand ?? '')
  const [basis, setBasis] = useState<'g' | 'ml'>(initial.basis)
  const [servingLabel, setServingLabel] = useState(initial.serving_label || (initial.serving_size ? '' : '100 g'))
  const [size, setSize] = useState(String(size0))
  const [vals, setVals] = useState<Record<string, string>>(() =>
    Object.fromEntries(FIELDS.map((f) => [f.key, perServing0[f.key] !== undefined ? String(Math.round(perServing0[f.key]! * 10) / 10) : ''])))
  const [more, setMore] = useState(FIELDS.some((f) => !f.main && perServing0[f.key] !== undefined))
  const [busy, setBusy] = useState(false)

  const sizeN = Number(size)
  const ok = name.trim() && sizeN > 0 && vals.kcal !== '' && Number.isFinite(Number(vals.kcal))

  const save = async () => {
    setBusy(true)
    try {
      const perServing = clean(Object.fromEntries(FIELDS.filter((f) => vals[f.key] !== '')
        .map((f) => [f.key, Number(vals[f.key])])), false)
      const per100 = per100From(perServing, sizeN) ?? {}
      const label = servingLabel.trim() || `${sizeN} ${basis}`
      const row = await saveCustomFood({
        id: initial.id, name: name.trim(), brand: brand.trim() || null, barcode: initial.barcode ?? null,
        basis, per100, servings: [{ label, grams: sizeN, default: true }],
        source: initial.source ?? 'manual', last_used_at: new Date().toISOString(),
      })
      onSaved(row as CustomFoodRow)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      {note && <p className="muted small">{note}</p>}
      <label htmlFor="cf-name">Name</label>
      <input id="cf-name" value={name} onChange={(e) => setName(e.target.value)} />
      <label htmlFor="cf-brand">Brand (optional)</label>
      <input id="cf-brand" value={brand} onChange={(e) => setBrand(e.target.value)} />
      {initial.barcode && <p className="muted small">Barcode {initial.barcode}</p>}

      <div className="grid2">
        <div>
          <label htmlFor="cf-serv">Serving</label>
          <input id="cf-serv" value={servingLabel} placeholder="e.g. 2/3 cup" onChange={(e) => setServingLabel(e.target.value)} />
        </div>
        <div>
          <label htmlFor="cf-size">Serving size</label>
          <div className="row" style={{ gap: '.3rem' }}>
            <input id="cf-size" inputMode="decimal" value={size} onChange={(e) => setSize(e.target.value)} />
            <select aria-label="Unit" value={basis} onChange={(e) => setBasis(e.target.value as 'g' | 'ml')} style={{ width: '4.5rem' }}>
              <option value="g">g</option><option value="ml">ml</option>
            </select>
          </div>
        </div>
      </div>

      <h2 style={{ fontSize: '.95rem' }}>Per serving</h2>
      <div className="grid2">
        {FIELDS.filter((f) => f.main || more).map((f) => (
          <div key={f.key}>
            <label htmlFor={`cf-${f.key}`} style={{ marginTop: '.4rem' }}>{f.label} ({f.unit})</label>
            <input id={`cf-${f.key}`} inputMode="decimal" value={vals[f.key]}
              onChange={(e) => setVals((v) => ({ ...v, [f.key]: e.target.value }))} />
          </div>
        ))}
      </div>
      {!more && <button className="linky" style={{ marginTop: '.7rem' }} onClick={() => setMore(true)}>More nutrients</button>}
      <button style={{ width: '100%' }} disabled={!ok || busy} onClick={save}>Save food</button>
    </div>
  )
}
